import React, { useState, useMemo, useEffect } from "react";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  X,
  Users,
  CheckSquare,
  Clock,
  CircleCheck,
  Download,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";
import { addDoc, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import InfoModal from "../../components/InfoModal";
import CaseLimitDialog from "../../components/CaseLimitDialog";
import { auth, db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import featureGatingService from "../../services/featureGatingService";
import { getNgtCaseDetails } from "../../services/ngtApi";
import { buildNgtCaseData } from "./ngtCaseMapper";
import { fetchUserMobile, scheduleHearingReminders } from "./ngtReminders";
import { uploadNgtOrderPdfs } from "./ngtFileSync";
import { STATUS_PILL_BASE, statusPillSoft } from "../../styles/statusPill";

const CASES_PER_PAGE = 10;

function NGTCaseList() {
  const location = useLocation();
  const navigate = useNavigate();
  const { globalUser } = useAuth();
  const userId = globalUser?.uid;

  const { cases = [], searchType = "party-name", cookies = null } = location.state || {};

  const [selectedCases, setSelectedCases] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowLoading, setRowLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ total: 0, exported: 0, currentCase: null });
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [summaryModal, setSummaryModal] = useState({ open: false, message: "" });
  const [showCaseLimitDialog, setShowCaseLimitDialog] = useState(false);
  const [caseLimitData, setCaseLimitData] = useState({ currentCount: 0, maxLimit: 0, planName: "" });

  // Client-side search across the visible NGT list fields. We keep each case's
  // original index so selection/export (which read cases[index]) stay correct
  // even while a filter is applied.
  const filteredCases = useMemo(() => {
    const withIdx = cases.map((item, idx) => ({ item, idx }));
    const q = searchQuery.trim().toLowerCase();
    if (!q) return withIdx;
    return withIdx.filter(({ item }) => {
      const fields = [
        item.srNo,
        item.caseNumber,
        item.diaryNumber,
        item.partyName,
        item.zonalBench,
        item.registrationDate,
        item.status,
      ];
      return fields.some((f) => f != null && String(f).toLowerCase().includes(q));
    });
  }, [cases, searchQuery]);

  // Summary counts for the stat cards — derived from the status field already
  // present on each record, no extra fetch.
  const statusCounts = useMemo(() => {
    const disposed = cases.filter((c) =>
      String(c.status || "").toLowerCase().includes("disposed")
    ).length;
    return { total: cases.length, disposed, pending: cases.length - disposed };
  }, [cases]);

  // Pagination
  const indexOfLastCase = currentPage * CASES_PER_PAGE;
  const indexOfFirstCase = indexOfLastCase - CASES_PER_PAGE;
  const currentCases = filteredCases.slice(indexOfFirstCase, indexOfLastCase);
  const totalPages = Math.max(1, Math.ceil(filteredCases.length / CASES_PER_PAGE));

  // Snap back to a valid page when the filter shrinks the list
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };
  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    return pageNumbers;
  };

  // Selection (keyed by each case's original index, so it survives filtering)
  const handleCaseSelect = (index) => {
    const next = new Set(selectedCases);
    next.has(index) ? next.delete(index) : next.add(index);
    setSelectedCases(next);
  };

  const handleSelectAll = () => {
    const next = new Set(selectedCases);
    const allSelected =
      filteredCases.length > 0 && filteredCases.every(({ idx }) => next.has(idx));
    if (allSelected) {
      filteredCases.forEach(({ idx }) => next.delete(idx));
    } else {
      filteredCases.forEach(({ idx }) => next.add(idx));
    }
    setSelectedCases(next);
  };

  const handlePageSelectAll = () => {
    const pageIndices = currentCases.map(({ idx }) => idx);
    const next = new Set(selectedCases);
    const allSelected = pageIndices.every((i) => next.has(i));
    pageIndices.forEach((i) => (allSelected ? next.delete(i) : next.add(i)));
    setSelectedCases(next);
  };

  // Keep "Select All" checkbox in sync as the filter/selection changes
  useEffect(() => {
    if (filteredCases.length === 0) {
      setSelectAll(false);
      return;
    }
    setSelectAll(filteredCases.every(({ idx }) => selectedCases.has(idx)));
  }, [filteredCases, selectedCases]);

  const checkCaseLimitBeforeExport = async (count, targetCollection = "pending") => {
    if (!userId) return false;
    const result = await featureGatingService.checkCaseLimitBeforeExport(userId, count, targetCollection);
    if (!result.canProceed) {
      setCaseLimitData({
        currentCount: result.currentCount,
        maxLimit: result.maxLimit,
        planName: result.planName,
      });
      setShowCaseLimitDialog(true);
      return false;
    }
    return true;
  };

  // Open a single case
  const handleCaseClick = async (caseItem, index) => {
    if (!caseItem?.caseUrl) {
      toast.error("Case link unavailable for this record.", { duration: 4000 });
      return;
    }
    setRowLoading(index);
    try {
      const details = await getNgtCaseDetails(searchType, caseItem.caseUrl, cookies);
      if (details?.success === false || (!details?.caseInfo && !details?.parties)) {
        toast.error(details?.error || "No details found for this case.", { duration: 5000 });
        return;
      }
      navigate("/portal/ngt/caseDetails", {
        state: { caseDetails: details, listItem: caseItem, searchType, cookies },
      });
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Failed to fetch case details.", {
        duration: 5000,
      });
    } finally {
      setRowLoading(null);
    }
  };

  // Bulk export
  const exportSelectedCases = async () => {
    const user = auth.currentUser;
    if (!user) {
      alert("User not authenticated");
      return;
    }
    const selectedIndices = Array.from(selectedCases);
    if (selectedIndices.length === 0) {
      alert("No cases selected to export");
      return;
    }

    const canProceed = await checkCaseLimitBeforeExport(selectedIndices.length, "pending");
    if (!canProceed) return;

    setExporting(true);
    setExportProgress({ total: selectedIndices.length, exported: 0, currentCase: "Preparing export..." });
    await new Promise((r) => setTimeout(r, 100));

    // Fetch user mobile once so each reminder event can populate the SMS field
    const userMobile = await fetchUserMobile(user.uid);

    let success = 0;
    let failed = 0;
    let duplicates = 0;
    const failedCases = [];

    // Current pending count for subscription tracking
    let currentPendingCount = 0;
    try {
      const pendingSnap = await getDocs(query(collection(db, "pending"), where("owner", "==", user.uid)));
      currentPendingCount = pendingSnap.size;
    } catch (e) {
      console.warn("Could not read pending count:", e);
    }

    for (const index of selectedIndices) {
      const caseItem = cases[index];
      if (!caseItem) continue;
      const label = caseItem.caseNumber || caseItem.diaryNumber || `#${index + 1}`;

      try {
        setExportProgress((p) => ({ ...p, currentCase: `Processing: ${label}` }));

        if (!caseItem.caseUrl) {
          failed++;
          failedCases.push(label);
          setExportProgress((p) => ({ ...p, exported: p.exported + 1 }));
          continue;
        }

        const details = await getNgtCaseDetails(searchType, caseItem.caseUrl, cookies);
        if (details?.success === false || (!details?.caseInfo && !details?.parties)) {
          failed++;
          failedCases.push(label);
          setExportProgress((p) => ({ ...p, exported: p.exported + 1 }));
          continue;
        }

        const { caseData, collectionStatus } = buildNgtCaseData(details, caseItem, user, searchType, cookies);

        // Duplicate check across pending / disposed
        let isDuplicate = false;
        if (caseData.caseNo) {
          for (const coll of ["pending", "disposed"]) {
            const snap = await getDocs(
              query(
                collection(db, coll),
                where("caseNo", "==", caseData.caseNo),
                where("owner", "==", user.uid)
              )
            );
            if (!snap.empty) {
              isDuplicate = true;
              break;
            }
          }
        }
        if (isDuplicate) {
          duplicates++;
          setExportProgress((p) => ({ ...p, exported: p.exported + 1, currentCase: `Skipped duplicate: ${label}` }));
          continue;
        }

        // Per-case limit check
        const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
          user.uid,
          1,
          collectionStatus === "disposed" ? "disposed" : "pending"
        );
        if (!limitResult.canProceed) {
          setCaseLimitData({
            currentCount: limitResult.currentCount,
            maxLimit: limitResult.maxLimit,
            planName: limitResult.planName,
          });
          setShowCaseLimitDialog(true);
          break;
        }

        // Subscription metadata for pending cases beyond free tier
        let subscriptionMetadata = null;
        if (collectionStatus === "pending") {
          subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
            user.uid,
            currentPendingCount
          );
        }

        const caseRef = await addDoc(collection(db, collectionStatus), {
          ...caseData,
          ...(subscriptionMetadata && { subscriptionMetadata }),
          timestamp: serverTimestamp(),
        });

        // Schedule 8 AM + 6 PM reminders on the next hearing date (no-op if
        // there isn't a future hearing date in the listing history).
        await scheduleHearingReminders({
          user,
          caseRef,
          caseNo: caseData.caseNo,
          partyName: caseData.petitionerName,
          adverseParty: caseData.respondentName,
          nextHearingDate: caseData.nextHearingDate,
          userMobile,
          caseStatus: collectionStatus,
        });

        // Import order PDFs from listing history into the case's
        // "Orders" folder under Files (silent per-PDF failures).
        setExportProgress((p) => ({ ...p, currentCase: `Saving orders: ${label}` }));
        await uploadNgtOrderPdfs({
          user,
          caseId: caseRef.id,
          caseNo: caseData.caseNo,
          caseCollection: collectionStatus,
          listingHistory: details?.listingHistory || [],
        });

        if (collectionStatus === "pending") currentPendingCount++;
        success++;
        setExportProgress((p) => ({ ...p, exported: p.exported + 1, currentCase: `Done: ${label}` }));
        await new Promise((r) => setTimeout(r, 80));
      } catch (err) {
        console.error(`Error exporting case ${label}:`, err);
        failed++;
        failedCases.push(label);
        setExportProgress((p) => ({ ...p, exported: p.exported + 1 }));
      }
    }

    await new Promise((r) => setTimeout(r, 400));
    setExporting(false);
    setExportProgress({ total: 0, exported: 0, currentCase: null });

    if (success > 0 && failed === 0 && duplicates === 0) {
      setIsInfoModalOpen(true);
    } else if (success > 0 || duplicates > 0 || failed > 0) {
      const lines = [];
      if (success > 0) lines.push(`${success} case(s) exported successfully.`);
      if (duplicates > 0) lines.push(`${duplicates} case(s) skipped — already in your profile.`);
      if (failed > 0)
        lines.push(`${failed} case(s) could not be exported — no data found.\nFailed: ${failedCases.join(", ")}`);
      setSummaryModal({ open: true, message: lines.join("\n\n") });
    }
  };

  return (
    <>
        <div className="min-h-screen bg-[#F8FAFC]">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            {/* Stepper */}
            <div className="mb-4"><FindCaseStepper current={3} /></div>

            {/* Header */}
            <div className="mb-4 flex items-center gap-3">
              <Link
                to="/portal/fetchfromngt"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50"
                title="Back to search"
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <h1 className="text-xl font-bold text-slate-900">NGT Case List</h1>
            </div>

            {/* Summary stat cards */}
            {cases.length > 0 && (
              <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
                {[
                  { label: "Total Found", value: statusCounts.total, Icon: Users, tint: "bg-indigo-50 text-indigo-500" },
                  { label: "Selected", value: selectedCases.size, Icon: CheckSquare, tint: "bg-blue-50 text-blue-500" },
                  { label: "Pending", value: statusCounts.pending, Icon: Clock, tint: "bg-green-50 text-green-500" },
                  { label: "Disposed", value: statusCounts.disposed, Icon: CircleCheck, tint: "bg-red-50 text-red-500" },
                ].map(({ label, value, Icon, tint }) => (
                  <div
                    key={label}
                    className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-2xl font-bold leading-tight text-slate-900">{value}</p>
                      <p className="truncate text-xs text-slate-500">{label}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

        {/* Row-click details loading overlay */}
        {rowLoading !== null && !exporting && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl px-8 py-7 shadow-2xl text-center max-w-sm w-full mx-4">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Loading…</h3>
              <p className="text-sm text-gray-600">Please wait a moment.</p>
            </div>
          </div>
        )}

        {/* Export Progress Modal */}
        {exporting && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Exporting Your Cases</h3>
              <p className="text-sm text-gray-600 mb-6">
                Please wait while we process your selected cases...
              </p>
              <div className="w-full bg-gray-200 rounded-full h-5 overflow-hidden mb-3">
                <div
                  className="h-full bg-blue-600 rounded-full transition-all duration-500"
                  style={{
                    width:
                      exportProgress.total > 0
                        ? `${(exportProgress.exported / exportProgress.total) * 100}%`
                        : "0%",
                  }}
                ></div>
              </div>
              <p className="text-sm font-semibold text-blue-600 mb-2">
                {exportProgress.exported} / {exportProgress.total}
              </p>
              {exportProgress.currentCase && (
                <p className="text-xs text-gray-600 truncate">{exportProgress.currentCase}</p>
              )}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                <p className="text-xs text-yellow-800">
                  Please do not close this window until the export is complete.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Cases Table Card */}
        <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {cases.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-base font-medium text-slate-700">No cases found</p>
              <Link
                to="/portal/fetchfromngt"
                className="mt-2 inline-block text-sm font-medium text-yellow-600 hover:underline"
              >
                Return to Search
              </Link>
            </div>
          ) : (
            <>
              {/* Toolbar — search + bulk actions */}
              <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 items-center gap-3">
                  <div className="relative w-full max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder="Search in results..."
                      className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                        title="Clear search"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {totalPages > 1 && filteredCases.length > 0 && (
                    <button
                      type="button"
                      onClick={handlePageSelectAll}
                      className="hidden h-10 shrink-0 rounded-lg border border-slate-200 px-4 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 md:block"
                    >
                      {currentCases.length > 0 && currentCases.every(({ idx }) => selectedCases.has(idx))
                        ? "Deselect Page"
                        : "Select Page"}
                    </button>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  {selectedCases.size > 0 && (
                    <span className="text-sm text-slate-500">{selectedCases.size} selected</span>
                  )}
                  <button
                    onClick={exportSelectedCases}
                    className={`flex h-10 items-center gap-2 rounded-lg px-5 text-sm font-semibold transition-colors ${
                      exporting || selectedCases.size === 0
                        ? "cursor-not-allowed bg-slate-100 text-slate-400"
                        : "bg-yellow-500 text-slate-900 hover:bg-yellow-400"
                    }`}
                    disabled={exporting || selectedCases.size === 0}
                  >
                    <Download className="h-4 w-4" />
                    {exporting
                      ? `Exporting (${exportProgress.exported}/${exportProgress.total})`
                      : `Export Selected (${selectedCases.size})`}
                  </button>
                </div>
              </div>

              {filteredCases.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-base font-medium text-slate-700">No cases match your search</p>
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table w-full border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <th className="w-12 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                            disabled={filteredCases.length === 0}
                            title="Select all"
                          />
                        </th>
                        <th className="px-4 py-3">Sr No</th>
                        <th className="px-4 py-3">Case Number</th>
                        <th className="px-4 py-3">Diary Number</th>
                        <th className="px-4 py-3">Party Name</th>
                        <th className="px-4 py-3">Zonal Bench</th>
                        <th className="px-4 py-3">Reg. Date</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="w-16 px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentCases.map(({ item: caseItem, idx: actualIndex }) => {
                        const isRowLoading = rowLoading === actualIndex;
                        const rowClickable = rowLoading === null;
                        const openCase = () => rowClickable && handleCaseClick(caseItem, actualIndex);
                        const cellClass = `px-4 py-3.5 text-sm text-slate-600 ${
                          rowClickable ? "cursor-pointer" : "cursor-wait"
                        }`;
                        return (
                          <tr
                            key={actualIndex}
                            className={`border-b border-slate-100 transition-colors duration-150 ${
                              selectedCases.has(actualIndex) ? "bg-yellow-50/60" : "hover:bg-slate-50"
                            } ${rowLoading !== null && !isRowLoading ? "opacity-50" : ""}`}
                          >
                            <td className="px-4 py-3.5">
                              <input
                                type="checkbox"
                                checked={selectedCases.has(actualIndex)}
                                onChange={() => handleCaseSelect(actualIndex)}
                                className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                              />
                            </td>
                            <td className={cellClass} onClick={openCase}>
                              {caseItem.srNo || actualIndex + 1}
                            </td>
                            <td className={`${cellClass} font-medium text-slate-900`} onClick={openCase}>
                              <div className="flex items-center gap-2">
                                {isRowLoading && (
                                  <svg className="h-4 w-4 animate-spin text-yellow-500" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                )}
                                <span>{caseItem.caseNumber || "—"}</span>
                              </div>
                            </td>
                            <td className={cellClass} onClick={openCase}>{caseItem.diaryNumber || "—"}</td>
                            <td className={cellClass} onClick={openCase}>{caseItem.partyName || "—"}</td>
                            <td className={cellClass} onClick={openCase}>{caseItem.zonalBench || "—"}</td>
                            <td className={cellClass} onClick={openCase}>{caseItem.registrationDate || "—"}</td>
                            <td className={cellClass} onClick={openCase}>
                              <span className={`${STATUS_PILL_BASE} ${statusPillSoft(caseItem.status)}`}>
                                {caseItem.status || "—"}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              <button
                                type="button"
                                onClick={openCase}
                                disabled={!rowClickable}
                                title="View case details"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-wait"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Footer — result range + pagination */}
              {filteredCases.length > 0 && (
                <div className="flex flex-col items-center gap-4 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between">
                  <p className="text-sm text-slate-500">
                    Showing {indexOfFirstCase + 1} to {Math.min(indexOfLastCase, filteredCases.length)} of{" "}
                    {filteredCases.length} results
                  </p>

                  {totalPages > 1 && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>

                      {currentPage > 3 && (
                        <>
                          <button
                            onClick={() => setCurrentPage(1)}
                            className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                          >
                            1
                          </button>
                          {currentPage > 4 && <span className="px-1 text-slate-400">...</span>}
                        </>
                      )}

                      {getPageNumbers().map((number) => (
                        <button
                          key={number}
                          onClick={() => setCurrentPage(number)}
                          className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition-colors ${
                            currentPage === number
                              ? "bg-yellow-500 text-slate-900"
                              : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {number}
                        </button>
                      ))}

                      {currentPage < totalPages - 2 && (
                        <>
                          {currentPage < totalPages - 3 && <span className="px-1 text-slate-400">...</span>}
                          <button
                            onClick={() => setCurrentPage(totalPages)}
                            className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                          >
                            {totalPages}
                          </button>
                        </>
                      )}

                      <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
          </div>
        </div>

      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        title="Export Successful"
        message={
          "All selected cases were exported successfully.\n\n" +
          "• Reminders scheduled at 8:00 AM and 6:00 PM on each case's next hearing date (where available).\n" +
          "• Order PDFs saved to the \"Orders\" folder under Files for each case."
        }
      />
      <InfoModal
        isOpen={summaryModal.open}
        onClose={() => setSummaryModal({ open: false, message: "" })}
        title="Export Summary"
        message={summaryModal.message}
      />
      <CaseLimitDialog
        isOpen={showCaseLimitDialog}
        onClose={() => setShowCaseLimitDialog(false)}
        currentCount={caseLimitData.currentCount}
        maxLimit={caseLimitData.maxLimit}
        planName={caseLimitData.planName}
      />
    </>
  );
}

export default NGTCaseList;
